import { Dispatch, useState } from 'react';

import { ChevronRightIcon, InfoIcon, UserPlusIcon } from 'assets/icons';
import cardStyles from 'common/settings-layout/settings-card.module.scss';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { HelperText } from 'common/helper-text';
import { ModalImportContributors } from 'common/invite-to-record-process/components/modal-import-contributors/modal-import-contributors';
import { PeopleTable } from 'common/people-table';
import { ShareDialog } from 'common/share-dialog';
import { albumShareTarget, vaultShareTarget } from 'helpers/sharing';
import { classnames, isReadOnlySubOrRestrictedVault } from 'helpers/utils';
import { RouteName, goToPage } from 'routes';
import styles from './contributors-section.module.scss';

type ContributorsSectionProps = {
  generalDispatch: Dispatch<any>;
  vault: VaultType;
  isOwner: boolean;
  refetchVault: () => Promise<void>;
};

const ContributorsSection: React.FC<ContributorsSectionProps> = ({
  vault,
  refetchVault,
}) => {
  const [showShare, setShowShare] = useState(false);
  const [showImportContributorModal, setShowImportContributorModal] =
    useState(false);
  // Reloads the people list after inviting or importing.
  const [refreshKey, setRefreshKey] = useState(0);

  const handleConfirm = () => {
    setShowImportContributorModal(false);
    setRefreshKey((key) => key + 1);
    refetchVault();
  };

  return (
    <div className={classnames(cardStyles.card, styles.contributorsSection)}>
      <div className={classnames(styles.headerCollaborators)}>
        <p className="text__title2__gray800">Collaborators</p>
        <Button
          buttonStyle={ButtonStyle.LinkColor}
          buttonSize={ButtonSize.Small}
          className={styles.collaboratorsPageLink}
          onClick={() =>
            goToPage(RouteName.Collaborators, {}, { vaultId: vault.id })
          }
        >
          See in Collaborators page
          <ChevronRightIcon />
        </Button>
      </div>
      <HelperText
        content="Editors can add, edit or delete any content. Contributors add content and change only their own. Viewers only look. Share an album or a single memory from its own page."
        icon={<InfoIcon className={styles.infoIcon} />}
      />
      <div className={styles.buttonContainer}>
        {!isReadOnlySubOrRestrictedVault(vault) && (
          <Button
            className={classnames(
              cardStyles.pillButton,
              cardStyles.pillPrimary,
              styles.addContributorButton,
            )}
            buttonStyle={ButtonStyle.Primary}
            onClick={() => setShowShare(true)}
          >
            <UserPlusIcon /> Invite
          </Button>
        )}
        <Button
          className={classnames(
            cardStyles.pillButton,
            cardStyles.pillSecondary,
            styles.addContributorButton,
          )}
          buttonStyle={ButtonStyle.SecondaryGray}
          onClick={() => setShowImportContributorModal(true)}
        >
          Import from another Vault
        </Button>
      </div>
      <PeopleTable
        vaultId={vault.id}
        vaultTitle={vault.title}
        refreshKey={refreshKey}
        limit={6}
      />

      {showShare && (
        <ShareDialog
          target={vaultShareTarget(vault)}
          choices={[
            vaultShareTarget(vault),
            ...(vault.allAlbums || [])
              .filter((album) => !album.isDefault)
              .map((album) => albumShareTarget(vault.id, album)),
          ]}
          onClose={() => {
            setShowShare(false);
            setRefreshKey((key) => key + 1);
          }}
        />
      )}

      {showImportContributorModal && (
        <ModalImportContributors
          userType="contributor"
          handleClose={() => setShowImportContributorModal(false)}
          handleConfirm={handleConfirm}
        />
      )}
    </div>
  );
};

export { ContributorsSection };
